import React, { useEffect, useState } from 'react';
import { Building2, HardHat, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { EMPTY_ROLE_REPAIR, RoleRepairStatus, repairRole, roleRepairStatus } from '@/lib/accountState';

/**
 * ACCOUNT TYPE REPAIR CARD.
 *
 * Shown ONLY when the server says this account carries company registration
 * data (company name, contact name, trades, service areas) while it is stored
 * as a project owner — the exact damage caused by the signup race fixed in
 * AuthContext. Everybody else never sees it.
 *
 * The decision and the write both happen in the account-role-repair edge
 * function: one repair per account, refused once the account has any purchase,
 * subscription, credit or homeowner listing, and every attempt audited.
 */
const AccountRoleFix: React.FC = () => {
  const { lang } = useI18n();
  const { profile, refreshProfile } = useAuth();
  const es = lang === 'es';
  const [status, setStatus] = useState<RoleRepairStatus>(EMPTY_ROLE_REPAIR);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!profile?.id || profile.user_type !== 'homeowner') return;
    (async () => {
      const s = await roleRepairStatus();
      if (active) setStatus(s);
    })();
    return () => {
      active = false;
    };
  }, [profile?.id, profile?.user_type]);

  if (!status.repairAvailable) return null;

  const fix = async (target: 'gc' | 'subcontractor') => {
    setError('');
    setBusy(target);
    const res = await repairRole(target);
    setBusy('');
    if (!res.ok) {
      setError(
        es
          ? 'No pudimos cambiar el tipo de cuenta automáticamente. Escriba a support@giggone.com y lo corregimos.'
          : 'We could not switch the account type automatically. Email support@giggone.com and we will correct it.',
      );
      return;
    }
    await refreshProfile();
  };

  return (
    <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
      <p className="flex items-center gap-2 text-base font-extrabold text-amber-900">
        <AlertTriangle className="h-5 w-5" />
        {es ? 'Su cuenta quedó guardada como propietario' : 'Your account was saved as a property owner'}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-amber-900">
        {es
          ? 'Su registro incluye nombre de empresa y oficios, así que se registró como contratista. Un error al crear la cuenta la guardó como propietario, por eso solo vio “publicar un trabajo”. Elija su tipo de cuenta correcto:'
          : 'Your signup includes a company name and trades, so you registered as a contractor. A fault while creating the account saved it as a property owner, which is why you only saw “post a job”. Choose your correct account type:'}
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => fix('gc')}
          disabled={busy !== ''}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          <Building2 className="h-4 w-4" />
          {busy === 'gc'
            ? es
              ? 'Cambiando…'
              : 'Switching…'
            : es
            ? 'Soy contratista general'
            : 'I am a general contractor'}
        </button>
        <button
          type="button"
          onClick={() => fix('subcontractor')}
          disabled={busy !== ''}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-50"
        >
          <HardHat className="h-4 w-4" />
          {busy === 'subcontractor'
            ? es
              ? 'Cambiando…'
              : 'Switching…'
            : es
            ? 'Soy subcontratista'
            : 'I am a subcontractor'}
        </button>
      </div>
      {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
    </div>
  );
};

export default AccountRoleFix;
